/**
 * ExploreFolderModal Store
 *
 * Manages state for the folder explorer modal: the folder being browsed, search params,
 * pagination, sort, loading state, etc. Page size is persisted at the WORKSHEET level,
 * while table filter and sort settings survive modal resets within the active session.
 */

import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { Folder, Item, SearchParams } from '@/explorer/exploreModal.types';
import type { SearchItemLocation } from '@/main/app.constants';
import type { RootEnum } from '@seeqdev/api/endpoint/FoldersApi';

export interface ExploreFolderModalState {
  /** ID of the folder the user landed on when the modal opened. */
  initialFolderId: string | undefined;
  /** Workbook ID associated with the explorer session, if any. */
  workbookId: string | undefined;
  /** Active location filter for the table (My Folder, Corporate, Shared, etc.). */
  tableFilter: SearchItemLocation;
  /** Total number of items matching the current query (for pagination). */
  itemTotalForTable: number;
  /** Page size for the table. Persisted. */
  pageSize: number;
  /** 1-based current page number. */
  pageNumber: number;
  /** Active search parameters sent to the folders API. */
  searchParams: Partial<SearchParams>;
  /** Items currently displayed in the table. */
  items: Item[];
  /** True while items are being loaded. */
  loading: boolean;
  /** Folder currently focused in the explorer. */
  folder: Folder | undefined;
  /** True when sort should be ascending. */
  sortAscending: boolean;
  /** Field the table is currently sorted by. Persisted. */
  sortProperty: string;
  /** True when the search results header should be displayed. */
  showResults: boolean;
  /** Root scope used for restricting the folders query (e.g., USERS). */
  tableRoot: RootEnum | undefined;
}

/** Subset of state persisted at the worksheet level. */
export type ExploreFolderModalDehydratedState = Pick<ExploreFolderModalState, 'pageSize'>;

const initialState: ExploreFolderModalState = Object.freeze({
  initialFolderId: undefined,
  workbookId: undefined,
  tableFilter: SEARCH_ITEM_LOCATIONS.MY_FOLDER,
  itemTotalForTable: 0,
  pageSize: 5,
  pageNumber: 1,
  searchParams: { types: ['Folder'] },
  items: [],
  loading: false,
  folder: undefined,
  sortAscending: false,
  sortProperty: 'name',
  showResults: false,
  tableRoot: undefined,
});

export const dehydrateExploreFolderModalState = (
  state: ExploreFolderModalState,
): ExploreFolderModalDehydratedState => ({
  pageSize: state.pageSize,
});

export interface ExploreFolderModalActions {
  setInitialFolderId: (initialFolderId: string | undefined) => void;
  setWorkbookId: (workbookId: string | undefined) => void;
  setTableFilter: (tableFilter: SearchItemLocation) => void;
  setItemTotalForTable: (itemTotalForTable: number) => void;
  setPageSize: (pageSize: number) => void;
  setPageNumber: (pageNumber: number) => void;
  setItems: (items: Item[]) => void;
  /**
   * Updates a single search parameter field. Passing `''` removes the field entirely;
   * any other value (including `undefined`) is assigned to the field.
   */
  setSearchParam: (field: keyof SearchParams, value: unknown) => void;
  setLoading: (loading: boolean) => void;
  setFolder: (folder: Folder | undefined) => void;
  setSort: (payload: { sortProperty: string; sortAscending: boolean }) => void;
  setShowResults: (showResults: boolean) => void;
  setTableRoot: (root: RootEnum | undefined) => void;
  /** Clears transient state while preserving in-session table preferences. */
  resetToSessionPreferences: () => void;
  reset: () => void;
}

const exploreFolderModalActionsFactory: ActionsFactory<ExploreFolderModalState, ExploreFolderModalActions> = (
  set,
  get,
  resetState,
) => ({
  setInitialFolderId: (initialFolderId) =>
    set((draft) => {
      draft.initialFolderId = initialFolderId;
    }),

  setWorkbookId: (workbookId) =>
    set((draft) => {
      draft.workbookId = workbookId;
    }),

  setTableFilter: (tableFilter) =>
    set((draft) => {
      draft.tableFilter = tableFilter;
    }),

  setItemTotalForTable: (itemTotalForTable) =>
    set((draft) => {
      draft.itemTotalForTable = itemTotalForTable;
    }),

  setPageSize: (pageSize) =>
    set((draft) => {
      draft.pageSize = pageSize;
    }),

  setPageNumber: (pageNumber) =>
    set((draft) => {
      draft.pageNumber = pageNumber;
    }),

  setItems: (items) =>
    set((draft) => {
      draft.items = items;
    }),

  setSearchParam: (field, value) =>
    set((draft) => {
      if (value === '') {
        delete (draft.searchParams as Record<string, unknown>)[field];
      } else {
        (draft.searchParams as Record<string, unknown>)[field] = value;
      }
    }),

  setLoading: (loading) =>
    set((draft) => {
      draft.loading = loading;
    }),

  setFolder: (folder) =>
    set((draft) => {
      draft.folder = folder;
    }),

  setSort: (payload) =>
    set((draft) => {
      draft.sortProperty = payload.sortProperty;
      draft.sortAscending = payload.sortAscending;
    }),

  setShowResults: (showResults) =>
    set((draft) => {
      draft.showResults = showResults;
    }),

  setTableRoot: (root) =>
    set((draft) => {
      draft.tableRoot = root;
    }),

  reset: resetState,
  resetToSessionPreferences: () => {
    const { pageSize, sortProperty, tableFilter } = get();
    resetState({ pageSize, sortProperty, tableFilter });
  },
});

const STORE_NAME = 'ExploreFolderModal';

const [ExploreFolderModalStoreProvider, useExploreFolderModalStoreInstance, useExploreFolderModalSelector] =
  createStore(STORE_NAME, initialState, exploreFolderModalActionsFactory);

export { ExploreFolderModalStoreProvider };

// Selector hooks
export const useExploreFolderModalInitialFolderId = () =>
  useExploreFolderModalSelector((state) => state.initialFolderId);

export const useExploreFolderModalWorkbookId = () => useExploreFolderModalSelector((state) => state.workbookId);

export const useExploreFolderModalTableFilter = () => useExploreFolderModalSelector((state) => state.tableFilter);

export const useExploreFolderModalItemTotalForTable = () =>
  useExploreFolderModalSelector((state) => state.itemTotalForTable);

export const useExploreFolderModalPageSize = () => useExploreFolderModalSelector((state) => state.pageSize);

export const useExploreFolderModalPageNumber = () => useExploreFolderModalSelector((state) => state.pageNumber);

export const useExploreFolderModalSearchParams = () => useExploreFolderModalSelector((state) => state.searchParams);

export const useExploreFolderModalItems = () => useExploreFolderModalSelector((state) => state.items);

export const useExploreFolderModalLoading = () => useExploreFolderModalSelector((state) => state.loading);

export const useExploreFolderModalFolder = () => useExploreFolderModalSelector((state) => state.folder);

export const useExploreFolderModalSortAscending = () => useExploreFolderModalSelector((state) => state.sortAscending);

export const useExploreFolderModalSortProperty = () => useExploreFolderModalSelector((state) => state.sortProperty);

export const useExploreFolderModalShowResults = () => useExploreFolderModalSelector((state) => state.showResults);

export const useExploreFolderModalTableRoot = () => useExploreFolderModalSelector((state) => state.tableRoot);

/** Returns the textSearch value from searchParams, or undefined. */
const selectSearchText = createSelector(
  (state: ExploreFolderModalState) => state.searchParams,
  (searchParams) => searchParams.textSearch,
);
export const useExploreFolderModalSearchText = () => useExploreFolderModalSelector(selectSearchText);

export const useExploreFolderModalActions = () => useExploreFolderModalStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateExploreFolderModalStore(initialStateOverrides?: Partial<ExploreFolderModalState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    exploreFolderModalActionsFactory,
    { storeName: STORE_NAME },
  );
}

export type ExploreFolderModalStore = ReturnType<typeof legacyCreateExploreFolderModalStore>;

/**
 * Registers the ExploreFolderModal store with the persistence pipeline so the
 * persisted subset survives across worksheet loads.
 */
export const usePersistExploreFolderModalStore = () => {
  const register = useRegisterPersistedStore();
  const store = useExploreFolderModalStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqExploreFolderModalStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateExploreFolderModalState,
    });
  }, [register, store]);
};
